import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Button, Card, CardContent, Spinner, Typography } from '@volvo/vcdk-react';
import { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ILegalContract, ITelemetryService } from '@/service/vpc/types/ITelemetryService';
import type { ReactNode } from 'react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import DigitalServiceModal, {
  type DigitalServiceModalHandle,
} from '@/components/modals/digital-service/DigitalServiceModal';
import { featuresName, isFeatureToggled } from '@/config/EnviromentConfig';
import { useUnitServices } from '@/hooks/useUnitServices';
import { loaderUnitServicesPage } from '@/loaders/unit.services.loader';
import ServiceCard, {
  type ServiceCardStatus,
} from '@/routes/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/services/_components/ServiceCard';
import { unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { legalContractDocumentUrl } from '@/service/vpc/vpcService';
import { telemetry } from '@/utils';
import { hasActiveRequest } from '@/utils/service-activation';

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/services/')({
  component: UnitServicesPage,
  errorComponent: ErrorBoundary,
  loader: loaderUnitServicesPage,
});

// Static PDF fallback filename per service (used when no market-matched contract is present).
const STATIC_DESCRIPTION_FILE: Record<ITelemetryService['serviceName'], (segment: 'Marine' | 'Industrial') => string> =
  {
    ActiveMonitoring: (segment) =>
      segment === 'Marine'
        ? 'Service-Description-Connected-Services-Extended'
        : 'Service-Description-Active-Monitoring',
    ActiveMonitoringCore: () => 'Service-Description-Connected-Services-Core',
    ActiveMonitoringEcWifi: () => 'Service-Description-Connected-Services-Extended',
    Penta_Position: () => 'Service-Description-Location-Services',
  };

export function UnitServicesPage() {
  const { t } = useTranslation();
  const { endCustomerUnitId } = useParams({ strict: false });
  const { isLoading, segment, services, allServicesToggle, getDeactivationServices, getActivationServices } =
    useUnitServices();
  const { data: unit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! }));
  const allServicesModalRef = useRef<DigitalServiceModalHandle>(null);

  if (isLoading) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Spinner size="medium" className="" />
      </div>
    );
  }

  const emptyState = (
    <Card>
      <CardContent>
        <Typography variant="heading4" className="mb-6">
          {t(($) => $.pages.unit.services.empty_state.title)}
        </Typography>
        <Typography variant="caption1" className="text-subtle">
          {t(($) => $.pages.unit.services.empty_state.message)}
        </Typography>
      </CardContent>
    </Card>
  );

  // Easy Connect units have their digital-service provisioning hidden. The Services tab is
  // suppressed in the unit headers, but guard direct navigation to this route as well.
  if (
    isFeatureToggled(featuresName.hideEasyConnectProvisioning) &&
    telemetry.isEasyConnectUnit(unit?.telemetryDeviceInfo)
  ) {
    return emptyState;
  }

  type CardModel = {
    service: ITelemetryService;
    status: ServiceCardStatus;
    title: ReactNode;
    description: ReactNode;
    checklist: string[];
    readMoreHref?: string;
    requiresLabel?: string;
  };

  // Prefer the market-matched serviceOffer contract over the static PDF fallback. Plain
  // navigation to the anonymous document route keeps the document name in the browser URL.
  const resolveReadMore = (
    contract: ILegalContract | undefined,
    staticFile: string
  ): Pick<CardModel, 'readMoreHref'> => ({
    readMoreHref: contract
      ? legalContractDocumentUrl(contract.uri)
      : `${import.meta.env.VITE_BASE_PATH}documents/${staticFile}.pdf`,
  });

  const cards: CardModel[] = services
    .filter((service) => service.supported !== false)
    .map((service) => {
      // Market-matched backend: industrial ActiveMonitoring carries a separate Remote Software
      // Updates contract plus a COMBINED_DESCRIPTION PDF (service description + remote readout
      // in one document) — "Read more" prefers the combined one when the backend sends it.
      const contracts = (service.serviceOffer?.legalContracts ?? []).filter((c) =>
        c.ssmContractType.includes('SERVICE_DESCRIPTION')
      );
      const rsuContract = contracts.find((c) => c.ssmContractType.includes('REMOTE_READOUT_SERVICE_DESCRIPTION'));
      const primaryContract = contracts.find((c) => c !== rsuContract);
      const combinedContract = (service.serviceOffer?.legalContracts ?? []).find((c) =>
        c.ssmContractType.includes('COMBINED_DESCRIPTION')
      );

      const title = t(
        ($) => $.pages.unit.services.list[service.serviceName as keyof typeof $.pages.unit.services.list][segment].title
      );

      const description = (
        <Trans
          i18nKey={($) =>
            $.pages.unit.services.list[service.serviceName as keyof typeof $.pages.unit.services.list][segment]
              .description
          }
          components={{
            ActiveMonitoring: <span />,
            Penta_Position: <span />,
            Remote_Software_Update: <span />,
            ActiveMonitoringCore: <span />,
            ActiveMonitoringEcWifi: <span />,
          }}
        />
      );

      // `checklist` is a string[] leaf; the selector API only returns arrays when `returnObjects` is set.
      const checklist = t(
        ($) =>
          $.pages.unit.services.list[service.serviceName as keyof typeof $.pages.unit.services.list][segment].checklist,
        { returnObjects: true }
      ) as string[];

      const readMore = resolveReadMore(
        combinedContract ?? primaryContract,
        STATIC_DESCRIPTION_FILE[service.serviceName](segment)
      );

      const isActive = service.status === 'Active';
      const requestSent = hasActiveRequest(service);
      // The backend keeps `canActivate` true when unmet dependencies are allowed to be
      // activated together/in-flight (Marine Extended bundles Core; Industrial Location
      // Services while Active Monitoring's request is pending) — only show "Requires X"
      // when the backend actually refuses activation.
      const requiresPrereq = !service.dependencyInfo.allDependenciesMet && !service.canActivate;
      const status: ServiceCardStatus = isActive
        ? 'active'
        : requestSent
          ? 'request-sent'
          : requiresPrereq
            ? 'requires-prerequisite'
            : 'activatable';

      const requiresLabel =
        status === 'requires-prerequisite'
          ? t(($) => $.pages.unit.services.requires_prefix, {
              serviceName: service.dependencyInfo.dependencies
                .map((d) =>
                  t(
                    ($) =>
                      $.pages.unit.services.list[d.serviceName as keyof typeof $.pages.unit.services.list][segment]
                        .title
                  )
                )
                .join(', '),
            })
          : undefined;

      return { service, status, title, description, checklist, ...readMore, requiresLabel };
    });

  // Same source as the tab gate (hasVisibleServices): no card, no page — never a blank div.
  if (cards.length === 0) return emptyState;

  const activeCards = cards.filter((c) => c.status === 'active');
  const availableCards = cards.filter((c) => c.status !== 'active');

  return (
    <div className="flex flex-col">
      {activeCards.length > 0 && (
        <section className="flex flex-col gap-6">
          <Typography variant="body">{t(($) => $.pages.unit.services.active_services_title)}</Typography>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-3">
            {activeCards.map((c) => (
              <ServiceCard
                key={c.service.serviceId}
                segment={segment}
                endCustomerUnitId={endCustomerUnitId!}
                title={c.title}
                description={c.description}
                checklist={c.checklist}
                readMoreHref={c.readMoreHref}
                services={getActivationServices(c.service)}
                deactivationServices={getDeactivationServices(c.service)}
                status={c.status}
                requiresLabel={c.requiresLabel}
              />
            ))}
          </div>
        </section>
      )}

      {activeCards.length > 0 && availableCards.length > 0 && <div className="border-border-subtle my-8 border-t" />}

      {availableCards.length > 0 && (
        <section className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Typography variant="body">{t(($) => $.pages.unit.services.available_services_title)}</Typography>
            {allServicesToggle.visible && (
              // YAGNI: no tooltip on the disabled button — product settled on plain disabled
              <Button
                variant="secondary"
                disabled={allServicesToggle.services.length === 0}
                onClick={() => allServicesModalRef.current?.openActivate()}
              >
                {t(($) => $.pages.unit.services.all_services.title)}
              </Button>
            )}
          </div>
          <DigitalServiceModal
            ref={allServicesModalRef}
            segment={segment}
            endCustomerUnitId={endCustomerUnitId!}
            services={allServicesToggle.services}
            deactivationServices={allServicesToggle.services}
          />
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
            {availableCards.map((c) => (
              <ServiceCard
                key={c.service.serviceId}
                segment={segment}
                endCustomerUnitId={endCustomerUnitId!}
                title={c.title}
                description={c.description}
                checklist={c.checklist}
                readMoreHref={c.readMoreHref}
                services={getActivationServices(c.service)}
                deactivationServices={getDeactivationServices(c.service)}
                status={c.status}
                requiresLabel={c.requiresLabel}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
